/** Shared formatters for the cost overview. Kept pure so they're unit-testable. */

/**
 * Money, with the precision scaled to the magnitude. Sub-dollar AI costs are
 * routine here (a single classification call is fractions of a cent), so
 * rounding everything to whole dollars would turn most rows into "$0" and hide
 * exactly the long tail this page exists to expose.
 */
export const formatMoney = (value, { compact = false } = {}) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const abs = Math.abs(value);
  if (compact && abs >= 1000) {
    return `$${(value / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  }
  const digits = abs === 0 ? 2 : abs < 0.01 ? 4 : abs < 1 ? 3 : abs < 1000 ? 2 : 0;
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US");
};

export const formatCompactNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  return String(value);
};

export const formatPercent = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
};

export const formatLatency = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

/**
 * Langfuse trace names are code identifiers
 * (e.g. "LoanApplication-step1PerformLoanTask-llm"). Humanize them for display
 * while keeping the raw value available as a tooltip — the raw name is what
 * you grep for in the backend, so it must stay recoverable.
 */
export const humanizeTraceName = (traceName) => {
  if (!traceName) return "Unknown";
  return traceName
    .replace(/-llm$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

/** Share of a total, guarding the divide-by-zero that an empty window gives. */
export const shareOf = (value, total) =>
  !total || total <= 0 ? 0 : (value || 0) / total;
